import React from 'react';
import { X, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

/** Live-feedback modal for Item / EBO / Sales bulk imports.
 *
 *  Two phases:
 *   1. Uploading — shows a progress bar driven by axios `onUploadProgress`.
 *   2. Done — shows the server-returned summary (inserted / updated / skipped)
 *      and a table of per-row errors. Users can close and re-upload.
 *
 *  Props:
 *    open       — bool
 *    onClose    — fn()
 *    title      — string (e.g. "Item Master import")
 *    phase      — 'uploading' | 'done' | 'error'
 *    progress   — number 0..100
 *    fileName   — string
 *    result     — { inserted, updated, skipped, total, errors: [{row, reason}], ... } | null
 *    errorMsg   — string (for phase='error')
 */
export default function ImportProgressModal({ open, onClose, title, phase, progress, fileName, result, errorMsg }) {
  if (!open) return null;

  const errors = result?.errors || [];
  const summary = result ? {
    inserted: result.inserted || 0,
    updated: result.updated || 0,
    skipped: result.skipped || result.skippedBadEmail || 0,
    total: result.total || 0,
  } : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="import-progress-modal"
      onClick={(e) => { if (e.target === e.currentTarget && phase !== 'uploading') onClose?.(); }}
    >
      <div className="fm-card w-full max-w-[680px] bg-white overflow-hidden max-h-[86vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7E2D3]">
          <div className="flex items-center gap-2 text-[15px] font-semibold">
            {phase === 'uploading' && <Loader2 size={16} className="animate-spin" />}
            {phase === 'done' && errors.length === 0 && <CheckCircle2 size={16} style={{ color: '#4D7C5F' }} />}
            {(phase === 'done' && errors.length > 0) && <AlertTriangle size={16} style={{ color: '#B78140' }} />}
            {phase === 'error' && <AlertTriangle size={16} style={{ color: '#B84A4A' }} />}
            {title}
          </div>
          {phase !== 'uploading' && (
            <button onClick={onClose} className="text-[#7a6f5c] hover:text-[#232323]" data-testid="import-progress-close">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="p-5 flex-1 overflow-y-auto">
          {fileName && (
            <div className="text-[12.5px] text-[#7a6f5c] mb-3 truncate" title={fileName}>
              File: <span className="text-[#232323] font-medium">{fileName}</span>
            </div>
          )}

          {phase === 'uploading' && (
            <div data-testid="import-progress-phase-uploading">
              <div className="text-[13px] text-[#3d3427] mb-2">Uploading & processing… please keep this tab open.</div>
              <div className="w-full h-3 rounded-full bg-[#EFEADC] overflow-hidden">
                <div
                  className="h-full transition-[width] duration-200 ease-linear"
                  style={{ width: `${Math.max(3, Math.min(100, progress))}%`, background: '#4D7C5F' }}
                  data-testid="import-progress-bar"
                />
              </div>
              <div className="text-[11.5px] text-[#7a6f5c] mt-1 text-right" data-testid="import-progress-pct">
                {Math.min(100, Math.round(progress))}%
              </div>
              {progress >= 100 && (
                <div className="text-[12px] text-[#7a6f5c] mt-2">Upload complete — server is validating rows…</div>
              )}
            </div>
          )}

          {phase === 'done' && summary && (
            <div data-testid="import-progress-phase-done">
              <div className="grid grid-cols-4 gap-3 mb-4">
                <SummaryBox testid="import-summary-total" label="Total rows" value={summary.total} color="#3d3427" />
                <SummaryBox testid="import-summary-inserted" label="Inserted" value={summary.inserted} color="#4D7C5F" />
                <SummaryBox testid="import-summary-updated" label="Updated" value={summary.updated} color="#3d3427" />
                <SummaryBox testid="import-summary-skipped" label="Skipped" value={summary.skipped + errors.length} color={errors.length > 0 ? '#B84A4A' : '#7a6f5c'} />
              </div>

              {errors.length === 0 ? (
                <div className="rounded-md bg-[#E7F2E9] border border-[#B4D7B8] px-3 py-2 text-[13px]" style={{ color: '#2f6b41' }} data-testid="import-progress-clean">
                  All rows imported cleanly. ✓
                </div>
              ) : (
                <div>
                  <div className="text-[12.5px] font-medium text-[#B84A4A] mb-2" data-testid="import-progress-error-count">
                    {errors.length} row{errors.length === 1 ? '' : 's'} could not be imported:
                  </div>
                  <div className="border border-[#E7E2D3] rounded-md overflow-hidden">
                    <table className="w-full text-[12.5px]" data-testid="import-progress-error-table">
                      <thead className="bg-[#FAF7EE]">
                        <tr className="text-left text-[11px] text-[#7a6f5c]">
                          <th className="px-3 py-2 font-medium w-[80px]">Excel row</th>
                          <th className="px-3 py-2 font-medium">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {errors.map((e, i) => (
                          <tr key={i} className="border-t border-[#EFEADC]" data-testid={`import-progress-error-row-${i}`}>
                            <td className="px-3 py-2 font-mono">{e.row}</td>
                            <td className="px-3 py-2 text-[#B84A4A]">{e.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-[11px] text-[#7a6f5c] mt-2">
                    Row numbers match your Excel file (header on row 1). Fix these rows and re-upload — successful rows above are already saved.
                  </div>
                </div>
              )}
            </div>
          )}

          {phase === 'error' && (
            <div data-testid="import-progress-phase-error" className="rounded-md bg-[#FBEFEF] border border-[#E8B8B8] px-3 py-3 text-[13px]" style={{ color: '#B84A4A' }}>
              {errorMsg || 'Import failed.'}
            </div>
          )}
        </div>

        {phase !== 'uploading' && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E7E2D3] bg-[#FAF7EE]">
            <button
              onClick={onClose}
              data-testid="import-progress-done-btn"
              className="px-4 py-1.5 rounded-md text-[13px] font-medium text-white"
              style={{ background: '#232323' }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryBox({ label, value, color, testid }) {
  return (
    <div className="border border-[#EFEADC] rounded-md px-3 py-2 bg-white" data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-wide text-[#7a6f5c]">{label}</div>
      <div className="text-[18px] font-semibold" style={{ color }}>{value}</div>
    </div>
  );
}
